import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeftIcon as ArrowLeft,
  ClockCounterClockwiseIcon as ClockCounterClockwise,
  CloudIcon as Cloud,
  TargetIcon as Target,
  GridFourIcon as GridFour,
  UsersIcon as Users,
} from "@phosphor-icons/react";
import {
  getWorkspace,
  updateWorkspace,
  deleteWorkspace,
  fetchAgents,
  listWorkspaceEvents,
  logWorkspaceEvent,
  listWorkspaceMembers,
} from "@/lib/api";
import { useAuth } from "@/lib/auth";
import WorkspaceHeader from "@/components/workspace/WorkspaceHeader";
import {
  TabButton,
  SessionsTab,
  DeliverablesTab,
} from "@/components/workspace/WorkspaceTabs";
import MembersTab from "@/components/workspace/MembersTab";
import EngagementLog from "@/components/workspace/EngagementLog";
import PromoteModal from "@/components/workspace/PromoteModal";
import { useWorkspacePromote } from "@/hooks/useWorkspacePromote";

export default function WorkspaceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [data, setData] = useState(null);
  const [agents, setAgents] = useState([]);
  const [tab, setTab] = useState("sessions");
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({ name: "", client_name: "", description: "" });
  const [copied, setCopied] = useState(false);
  const [events, setEvents] = useState([]);
  const [eventsFetchedAt, setEventsFetchedAt] = useState(null);
  const [showEvents, setShowEvents] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [memberCount, setMemberCount] = useState(0);
  const [myRole, setMyRole] = useState(null);

  // ---------- Data loaders ----------
  const load = useCallback(async () => {
    try {
      const d = await getWorkspace(id);
      setData(d);
      setForm({
        name: d.workspace.name,
        client_name: d.workspace.client_name || "",
        description: d.workspace.description || "",
      });
    } catch (e) {
      void e;
      navigate("/workspaces");
    }
  }, [id, navigate]);

  const loadEvents = useCallback(async () => {
    try {
      const ev = await listWorkspaceEvents(id, 50);
      setEvents(ev || []);
      setEventsFetchedAt(Date.now());
    } catch (e) {
      void e; // engagement log is best-effort — silent on failure
    }
  }, [id]);

  const loadMembers = useCallback(async () => {
    try {
      const m = await listWorkspaceMembers(id);
      const total = (m.members?.length || 0) + (m.pending?.length || 0);
      setMemberCount(total);
      const me = (m.members || []).find((x) => x.user_id === currentUser?.id);
      setMyRole(me?.role || (currentUser?.role === "admin" ? "owner" : null));
    } catch (e) {
      void e;
    }
  }, [id, currentUser]);

  useEffect(() => {
    load();
    fetchAgents().then(setAgents);
    loadEvents();
    loadMembers();
  }, [load, loadEvents, loadMembers]);

  // 30s background poll for the engagement log. Runs only when the log
  // is expanded AND the tab is visible — saves backend load and avoids
  // surprising the user with a refresh they can't see anyway. Returning
  // to the tab triggers an immediate refresh so they never stare at
  // stale data.
  useEffect(() => {
    if (!showEvents) return undefined;

    const POLL_MS = 30_000;
    let intervalId = null;

    const start = () => {
      if (intervalId) return;
      intervalId = setInterval(() => {
        if (document.visibilityState === "visible") loadEvents();
      }, POLL_MS);
    };
    const stop = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        loadEvents();
        start();
      } else {
        stop();
      }
    };

    if (document.visibilityState === "visible") start();
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [showEvents, loadEvents]);

  // ---------- Promote workflow (extracted to hook) ----------
  const promote = useWorkspacePromote({
    workspaceId: id,
    refresh: load,
    refreshEvents: loadEvents,
  });

  // ---------- Header actions ----------
  const handleSave = useCallback(async () => {
    await updateWorkspace(id, form);
    setEditMode(false);
    await load();
  }, [id, form, load]);

  const handleDelete = useCallback(async () => {
    if (!window.confirm("Delete this workspace? Sessions and deliverables remain but are unlinked.")) return;
    await deleteWorkspace(id);
    navigate("/workspaces");
  }, [id, navigate]);

  const handleShare = useCallback(() => {
    if (!data) return;
    // Use the SSR-lite preview URL so the link unfurls properly in Slack /
    // LinkedIn / X / Discord / email clients with the workspace-scoped OG
    // card. Humans hitting this URL get an instant JS redirect to /share/{token}.
    const url = `${window.location.origin}/api/share/${data.workspace.share_token}/preview.html`;
    navigator.clipboard.writeText(url).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    logWorkspaceEvent(id, "share_link_copied").then(() => loadEvents());
  }, [data, id, loadEvents]);

  const handleExportCombinedPdf = useCallback(async () => {
    if (!data) return;
    const ready = data.deliverables.filter((d) => d.status === "ready");
    if (ready.length === 0) {
      toast.error("No ready deliverables to export. Generate at least one first.");
      return;
    }
    setExportingPdf(true);
    try {
      const { exportWorkspaceCombinedPdf } = await import("@/lib/combinedPdf");
      const { getDeliverable } = await import("@/lib/api");
      const full = await Promise.all(ready.map((d) => getDeliverable(d.id)));
      await exportWorkspaceCombinedPdf({
        workspace: data.workspace,
        deliverables: full,
      });
      toast.success(
        `Combined PDF exported (${full.length} deliverable${full.length === 1 ? "" : "s"}).`
      );
      logWorkspaceEvent(id, "combined_pdf_exported", {
        count: full.length,
      }).then(() => loadEvents());
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "PDF export failed.");
    } finally {
      setExportingPdf(false);
    }
  }, [data, id, loadEvents]);

  const agentName = useCallback(
    (aid) => agents.find((a) => a.id === aid)?.name || aid,
    [agents]
  );

  const toggleEvents = useCallback(() => {
    setShowEvents((s) => {
      if (!s) loadEvents();
      return !s;
    });
  }, [loadEvents]);

  if (!data) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 text-center text-[var(--ml-text-secondary)]">
        Loading workspace…
      </div>
    );
  }

  const { workspace, sessions, deliverables } = data;
  const isOwner = myRole === "owner" || currentUser?.role === "admin";

  return (
    <div
      className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10"
      data-testid="workspace-detail-page"
    >
      <Link to="/workspaces" className="ml-btn-ghost mb-4" data-testid="back-to-workspaces">
        <ArrowLeft size={14} /> All Workspaces
      </Link>

      <WorkspaceHeader
        workspace={workspace}
        workspaceId={id}
        editMode={editMode}
        setEditMode={setEditMode}
        form={form}
        setForm={setForm}
        copied={copied}
        exportingPdf={exportingPdf}
        onSave={handleSave}
        onDelete={handleDelete}
        onShare={handleShare}
        onExportCombinedPdf={handleExportCombinedPdf}
        onOpenPromote={promote.open}
      />

      {/* Tabs — Iter 54: scroll sideways on phones instead of widening the page */}
      <div className="flex gap-1 border-b border-[var(--ml-border)] mb-6 overflow-x-auto" data-testid="workspace-tab-strip">
        <TabButton
          active={tab === "sessions"}
          onClick={() => setTab("sessions")}
          testid="tab-sessions"
        >
          Sessions ({sessions.length})
        </TabButton>
        <TabButton
          active={tab === "deliverables"}
          onClick={() => setTab("deliverables")}
          testid="tab-deliverables"
        >
          Deliverables ({deliverables.length})
        </TabButton>
        <TabButton
          active={tab === "members"}
          onClick={() => setTab("members")}
          testid="tab-members"
        >
          <span className="inline-flex items-center gap-1.5">
            <Users size={13} weight="bold" />
            Members ({memberCount})
          </span>
        </TabButton>
        {workspace.source_system_type === "SAP_ECC_IBMI" && (
          <Link
            to={`/workspaces/${id}/sap-ecc`}
            data-testid="sap-ecc-nav-link"
            className="px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-1.5 border-transparent text-[#0f62fe] hover:text-[var(--ml-primary)] whitespace-nowrap shrink-0"
          >
            <Cloud size={13} weight="bold" />
            SAP ECC
          </Link>
        )}
        <Link
          to={`/workspaces/${id}/risk-brief`}
          data-testid="risk-brief-nav-link"
          className="px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-1.5 border-transparent text-[var(--ml-text-secondary)] hover:text-[var(--ml-primary)] whitespace-nowrap shrink-0"
          title="Top-10 stuck items composed from heat-map + business rules — slide-ready"
        >
          <Target size={13} weight="bold" />
          Risk brief
        </Link>
        <Link
          to={`/workspaces/${id}/dispositions`}
          data-testid="dispositions-nav-link"
          className="px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-1.5 border-transparent text-[var(--ml-text-secondary)] hover:text-[var(--ml-primary)] whitespace-nowrap shrink-0"
          title="Per-object technical treatment × migration disposition — proposed from evidence, validated by you"
        >
          <GridFour size={13} weight="bold" />
          Dispositions
        </Link>
        <button
          onClick={toggleEvents}
          data-testid="toggle-engagement-log-btn"
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ml-auto flex items-center gap-1.5 whitespace-nowrap shrink-0 ${
            showEvents
              ? "border-[var(--ml-primary)] text-[var(--ml-primary)]"
              : "border-transparent text-[var(--ml-text-secondary)] hover:text-[var(--ml-text-primary)]"
          }`}
        >
          <ClockCounterClockwise size={14} weight="bold" />
          Engagement log ({events.length})
        </button>
      </div>

      {showEvents && <EngagementLog events={events} lastFetchedAt={eventsFetchedAt} />}

      {tab === "sessions" && (
        <SessionsTab sessions={sessions} agentName={agentName} />
      )}
      {tab === "deliverables" && <DeliverablesTab deliverables={deliverables} />}
      {tab === "members" && (
        <MembersTab
          workspaceId={id}
          isOwner={isOwner}
          currentUserId={currentUser?.id}
        />
      )}

      {promote.showPromote && (
        <PromoteModal
          workspace={workspace}
          deliverableCount={deliverables.length}
          readyDeliverableCount={
            deliverables.filter((d) => d.status === "ready").length
          }
          audienceNote={promote.audienceNote}
          setAudienceNote={promote.setAudienceNote}
          promoting={promote.promoting}
          result={promote.result}
          emailCopied={promote.emailCopied}
          onClose={promote.close}
          onPromote={promote.promote}
          onCopyEmail={promote.copyEmailBody}
          onOpenEmailClient={promote.openInEmailClient}
        />
      )}
    </div>
  );
}
